/**
 * Tire Pressure Engine v2 — Zenvio Trip Planner
 * ==============================================
 * Production-grade tire pressure model with physics-based foundation.
 *
 * Ported from /mnt/user-data/outputs/tire_pressure_engine_v2_20260517_0940.py
 * (Python reference implementation, 8/8 sanity tests passing).
 *
 * Engine design contract:
 *   - Takes ambient_temp_c as a REQUIRED input (engine doesn't fetch weather)
 *   - All other context (user pressure, days since check, trip duration, etc.)
 *     is OPTIONAL with graceful degradation
 *   - Returns a fully decomposed result for auditability
 *
 * References anchoring every constant
 * -----------------------------------
 * [1] Schwall et al. 2016 SAE 2016-01-1574 — Tesla Model S fleet TPMS telemetry
 *     → temperature coefficient empirically validated
 *
 * [2] Zhu, Han, Zhao 2019 Sensors 19:2560 doi:10.3390/s19112560
 *     → physics: f_tor ∝ √K_t; K_t = g(geometry)·p + h(material)
 *     → Michelin 205/55R16: p_tire [kPa] = 1.60·f² − 132.37·f + 2856.54
 *
 * [3] Matsubara et al. 2015 SAE 2015-01-2193
 *     → Sidewall stiffness: K_sidewall = K_pressure(p) + K_material(rubber)
 *
 * [4] Persson, Gustafsson, Drevö 2002 SAE 2002-01-1250 — Nira Dynamics
 *     → THE production iTPMS algorithm; 80M+ vehicles
 *
 * [5] Waddell, Napier, Tracey 2009 Rubber Chem Tech 82:229-243
 *     → Permeation: air 1-2 psi/month = 7-14 kPa/month; N2 ~1/3 reduction
 *
 * [6] USPTO 7,991,523 — Continental Automotive iTPMS production patent
 *
 * [7] Industry consensus (Continental, General, Les Schwab): 1 psi/10°F ≈ 0.69 kPa/°C
 *
 * [8] Heliyon Prasad & Gudipalli 2024 doi:10.1016/j.heliyon.2024.e41494
 *     → Crr × p^0.5 exponent for under-inflation penalty
 */

// ============ EMPIRICAL CONSTANTS (each cited above) ============

export const KPA_PER_DEG_C = 0.69;                    // refs [1], [7]
export const REFERENCE_TEMP_C = 20.0;                 // OEM placard reference temperature

export const PERMEATION_KPA_PER_MONTH_AIR = 10.0;     // ref [5] midpoint of 7-14
export const PERMEATION_KPA_PER_MONTH_N2 = 6.7;       // ref [5] ~1/3 reduction

export const WARMING_KPA_SUSTAINED = 5.0;             // refs [1], [6]
export const WARMING_RAMP_MIN = 30.0;                 // minutes to sustained warming
export const WARMING_HIGHWAY_BONUS = 3.0;             // extra warming above 80 km/h

export const CRR_PRESSURE_EXPONENT = 0.5;             // ref [8]
export const CRR_MULTIPLIER_MIN = 0.85;               // over-inflation benefit cap
export const CRR_MULTIPLIER_MAX = 1.40;               // under-inflation penalty cap

/**
 * Estimate tire pressure from torsion resonance frequency.
 *
 * Reference: Zhu, Han, Zhao 2019 doi:10.3390/s19112560.
 * Calibrated for Michelin 205/55R16. Other tire sizes need their own fit.
 * Validated points: 150 kPa→45.4 Hz; 250→50.2; 350→53.2.
 *
 * Used only when OBD2/CAN wheel-speed FFT telemetry is available (Phase 2).
 */
export function pressureFromFrequencyMichelin205_55_16(f_hz) {
  return 1.60 * f_hz * f_hz - 132.37 * f_hz + 2856.54;
}

// ============ MAIN ESTIMATION FUNCTION ============

/**
 * Estimate effective tire pressure during the trip.
 *
 * @param {Object} inp Input context.
 * @param {number} inp.spec_kpa             REQUIRED: placard cold spec pressure.
 * @param {number} [inp.user_input_kpa]     User-reported cold pressure (defaults to spec).
 * @param {number} [inp.ambient_temp_c]     Ambient temperature in °C (REQUIRED for temp correction).
 * @param {number} [inp.days_since_last_check]  Days since last tire-pressure check.
 * @param {number} [inp.trip_duration_min]  Trip duration in minutes (for warming model).
 * @param {number} [inp.avg_speed_kmh]      Average speed in km/h (for highway bonus).
 * @param {string} [inp.inflation_gas]      'air' (default) or 'n2'.
 * @param {number} [inp.measured_frequency_hz]  Phase-2: wheel-speed FFT torsion freq.
 * @param {string} [inp.tire_size_code]     e.g. '205/55R16' — for freq curve lookup.
 *
 * @returns {Object} TirePressureResult with every term decomposed:
 *   cold_input_kpa, temp_correction_kpa, permeation_loss_kpa, warming_gain_kpa,
 *   effective_kpa, underinflation_pct, crr_multiplier, method, notes
 */
export function estimateEffectivePressure(inp) {
  const notes = [];

  // ---- 1. Cold pressure (user input or fallback to spec) ----
  let cold_kpa;
  if (inp.user_input_kpa != null) {
    cold_kpa = inp.user_input_kpa;
  } else {
    cold_kpa = inp.spec_kpa;
    notes.push('user_input_kpa missing; assumed at spec');
  }

  // ---- 2. Temperature correction ----
  let temp_corr;
  if (inp.ambient_temp_c != null) {
    temp_corr = (inp.ambient_temp_c - REFERENCE_TEMP_C) * KPA_PER_DEG_C;
  } else {
    temp_corr = 0.0;
    notes.push('ambient_temp_c missing; no temp correction applied');
  }

  // ---- 3. Permeation loss ----
  let permeation;
  if (inp.days_since_last_check != null) {
    const rate = inp.inflation_gas === 'n2'
      ? PERMEATION_KPA_PER_MONTH_N2
      : PERMEATION_KPA_PER_MONTH_AIR;
    const months_elapsed = inp.days_since_last_check / 30.0;
    permeation = -rate * months_elapsed;
  } else {
    permeation = 0.0;
    notes.push('days_since_last_check missing; no permeation applied');
  }

  // ---- 4. In-trip warming (with highway bonus) ----
  let warming = 0.0;
  if (inp.trip_duration_min != null && inp.trip_duration_min > 5) {
    // Ramp from 0 to sustained warming over WARMING_RAMP_MIN minutes
    const warming_factor = Math.min(1.0, inp.trip_duration_min / WARMING_RAMP_MIN);
    warming = WARMING_KPA_SUSTAINED * warming_factor;
    // Highway bonus: sustained speed above 80 km/h adds extra warming
    if (inp.avg_speed_kmh != null && inp.avg_speed_kmh > 80) {
      const highway_factor = Math.min(1.0, (inp.avg_speed_kmh - 80) / 30);
      warming += WARMING_HIGHWAY_BONUS * highway_factor * warming_factor;
    }
  }

  // ---- 5. Compose effective pressure ----
  let effective_kpa = cold_kpa + temp_corr + permeation + warming;

  // ---- 6. OPTIONAL: validate against measured frequency (Phase 2 telemetry) ----
  let method = 'physics-only';
  if (inp.measured_frequency_hz != null && inp.tire_size_code === '205/55R16') {
    const freq_pressure = pressureFromFrequencyMichelin205_55_16(inp.measured_frequency_hz);
    const delta = Math.abs(effective_kpa - freq_pressure);
    if (delta < 20) {
      method = 'physics+frequency-validated';
      notes.push(`freq-based estimate ${freq_pressure.toFixed(1)} kPa agrees within ${delta.toFixed(1)} kPa`);
    } else {
      method = 'frequency-measured';
      notes.push(`physics model said ${effective_kpa.toFixed(1)} kPa, freq-based ${freq_pressure.toFixed(1)} kPa; using measured`);
      effective_kpa = freq_pressure;
    }
  }

  // Clamp to physical floor
  if (effective_kpa < 100.0) {
    notes.push(`effective_kpa was ${effective_kpa.toFixed(1)}, clamped to 100 (physical floor)`);
    effective_kpa = 100.0;
  }

  // ---- 7. Compute Crr penalty ----
  const pressure_ratio = effective_kpa / inp.spec_kpa;
  let crr_mult = Math.pow(pressure_ratio, -CRR_PRESSURE_EXPONENT);
  crr_mult = Math.max(CRR_MULTIPLIER_MIN, Math.min(CRR_MULTIPLIER_MAX, crr_mult));

  const underinflation_pct = (inp.spec_kpa - effective_kpa) / inp.spec_kpa * 100;

  return {
    cold_input_kpa: round1(cold_kpa),
    temp_correction_kpa: round1(temp_corr),
    permeation_loss_kpa: round1(permeation),
    warming_gain_kpa: round1(warming),
    effective_kpa: round1(effective_kpa),
    underinflation_pct: round1(underinflation_pct),
    crr_multiplier: round3(crr_mult),
    method,
    notes,
  };
}

/**
 * Sanity checks on inputs. Returns an array of human-readable error strings;
 * empty array if all inputs are within plausible physical envelopes.
 */
export function validateInputs(inp) {
  const errors = [];
  if (inp.spec_kpa < 150 || inp.spec_kpa > 400) {
    errors.push(`spec_kpa=${inp.spec_kpa} outside passenger car range (150-400)`);
  }
  if (inp.user_input_kpa != null && (inp.user_input_kpa < 100 || inp.user_input_kpa > 500)) {
    errors.push(`user_input_kpa=${inp.user_input_kpa} physically implausible`);
  }
  if (inp.ambient_temp_c != null && (inp.ambient_temp_c < -40 || inp.ambient_temp_c > 55)) {
    errors.push(`ambient_temp_c=${inp.ambient_temp_c} outside extreme weather envelope`);
  }
  if (inp.measured_frequency_hz != null && (inp.measured_frequency_hz < 30 || inp.measured_frequency_hz > 80)) {
    errors.push(`measured_frequency_hz=${inp.measured_frequency_hz} outside expected 35-80 Hz range`);
  }
  if (inp.inflation_gas != null && inp.inflation_gas !== 'air' && inp.inflation_gas !== 'n2') {
    errors.push(`inflation_gas=${inp.inflation_gas} must be 'air' or 'n2'`);
  }
  return errors;
}

// ============ HELPERS ============

function round1(x) { return Math.round(x * 10) / 10; }
function round3(x) { return Math.round(x * 1000) / 1000; }
